import type { CSSProperties } from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import styled from 'styled-components'
import { rem } from 'polished'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import { capitalise } from 'lib/string/stringUtils'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { StripeLuxPlusOffSessionPaymentCard } from 'checkout/lib/luxPlusCheckoutCardBackup'
import getLuxPlusCardExpiresBeforeRenewal from 'luxPlus/lib/getLuxPlusCardExpiresBeforeRenewal'

const BackupCardRowShell = styled.div<{ $showReorderControls?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$showReorderControls ? `${rem(16)} ${rem(12)} minmax(0, 1fr)` : 'minmax(0, 1fr)'};
  gap: ${rem(12)};
  align-items: start;
  width: 100%;
`

const DragHandle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(16)};
  height: ${rem(24)};
  margin-top: ${rem(8)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  cursor: grab;
  touch-action: none;

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    cursor: default;
  }

  &:active {
    cursor: grabbing;
  }
`

const PositionText = styled(BodyText)`
  margin-top: ${rem(12)};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const BackupCardStack = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(4)};
`

const BackupCardPill = styled.div<{ $hasWarning?: boolean }>`
  display: flex;
  min-width: 0;
  min-height: ${rem(40)};
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  padding: ${rem(8)};
  border: 1px solid ${props => props.$hasWarning ? props.theme.palette.messaging.critical.normalForeground : props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CardContent = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(12)};
`

const CardBrandGroup = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(8)};
`

const CardLogoFrame = styled.div`
  display: flex;
  width: ${rem(34)};
  height: ${rem(24)};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(4)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const CardLogo = styled.img`
  max-width: ${rem(24)};
  max-height: ${rem(16)};
`

const ExpiryWarningNotice = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.messaging.critical.normalForeground};
`

interface Props {
  isRemoveDisabled?: boolean
  isRemoving?: boolean
  isReorderDisabled?: boolean
  offSessionCard: StripeLuxPlusOffSessionPaymentCard
  onRemove: (cardId: string) => void
  position: number
  renewalDate?: string
  showReorderControls?: boolean
}

function LuxPlusBackupPaymentMethodRow(props: Props) {
  const {
    isRemoveDisabled,
    isRemoving,
    isReorderDisabled,
    offSessionCard,
    onRemove,
    position,
    renewalDate,
    showReorderControls = true,
  } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const expiresBeforeRenewal = getLuxPlusCardExpiresBeforeRenewal(offSessionCard, renewalDate)

  const handleRemove = useCallback(() => {
    showModal(<Modal
      title={intl.formatMessage({
        id: '0yFTZFzIKIHC1Rhcjasd',
        defaultMessage: 'Remove backup payment method?',
        description: 'Confirmation modal title before removing a LuxPlus+ backup payment method',
      })}
      primaryActionText={intl.formatMessage({
        id: 'JmIqJQxwb1KXCC8ShuBF',
        defaultMessage: 'Remove',
        description: 'Action button label to confirm card removal in the payment vendor integration',
      })}
      secondaryActionText={intl.formatMessage({
        id: 'D5a4vAMZ8HcH_btGiwLn',
        defaultMessage: 'Cancel',
        description: 'Button text to cancel card removal in the cancellation or refund flow',
      })}
      onPrimaryActionClick={(_event, resolve) => {
        onRemove(offSessionCard.id)
        resolve()
      }}
      onSecondaryActionClick={(_event, resolve) => {
        resolve()
      }}
      size="S"
    >
      <BodyText variant="medium">
        {intl.formatMessage({
          id: 'NjmPGxlGR2Q6ruwGY3UR',
          defaultMessage: 'Are you sure you want to remove this card ending in {last4} as a backup payment method?',
          description: 'Confirmation modal body text before removing a LuxPlus+ backup payment method',
        }, { last4: offSessionCard.card.last4 })}
      </BodyText>
    </Modal>)
  }, [intl, offSessionCard.card.last4, offSessionCard.id, onRemove, showModal])
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: offSessionCard.id, disabled: isReorderDisabled })

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }

  const removeLinkText = isRemoving ? intl.formatMessage({
    id: 'J83Bd4KJEieViQ3w1Y3s',
    defaultMessage: 'Removing',
    description: 'Temporary action text while removing a LuxPlus+ backup payment method',
  }) : intl.formatMessage({
    id: '5n88mGHnv06qsH2vYBnP',
    defaultMessage: 'Remove',
    description: 'Link text to remove a LuxPlus+ backup payment method',
  })

  return <BackupCardRowShell ref={setNodeRef} style={style} $showReorderControls={showReorderControls}>
    {showReorderControls && <>
      <DragHandle
        type="button"
        disabled={isReorderDisabled}
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={intl.formatMessage({
          id: 'gdOO39gVO5nvqpatSaOx',
          defaultMessage: 'Reorder backup payment method',
          description: 'Accessible label for dragging a LuxPlus+ backup payment method to a different priority',
        })}
      >
        <LineBarsIcon size="XS" />
      </DragHandle>
      <PositionText variant="small" weight="bold">{position}</PositionText>
    </>}
    <BackupCardStack>
      <BackupCardPill $hasWarning={expiresBeforeRenewal}>
        <CardContent>
          <CardBrandGroup>
            {offSessionCard.card.logo && <CardLogoFrame><CardLogo src={offSessionCard.card.logo} alt="" /></CardLogoFrame>}
            <BodyText variant="small" weight="bold" wrap="no-wrap">
              {intl.formatMessage({
                id: 'USEMWCFHetSO4VVbzAyh',
                defaultMessage: '{brand} ******** {last4}',
                description: 'Backup payment card label in LuxPlus+ account management',
              }, { brand: capitalise(offSessionCard.card.brand), last4: offSessionCard.card.last4 })}
            </BodyText>
          </CardBrandGroup>
          <BodyText variant="small" wrap="no-wrap">
            {intl.formatMessage({
              id: '8SMwGT0PKVvGkpmKUwaa',
              defaultMessage: 'Exp. {expiry}',
              description: 'Label displaying the expiration date of a saved payment card in the user account',
            }, { expiry: offSessionCard.card.expiry })}
          </BodyText>
        </CardContent>
        <TextLink size="caption" colour="neutral-one" onClick={handleRemove} disabled={isRemoveDisabled || isRemoving}>
          {removeLinkText}
        </TextLink>
      </BackupCardPill>
      {expiresBeforeRenewal && <ExpiryWarningNotice>
        <LineInfoCircleIcon size="XS" colour="critical" />
        <BodyText variant="small" colour="critical">
          {intl.formatMessage({
            id: 'djB2qpqGAWG963JYhTw1',
            defaultMessage: 'Expires before your next renewal',
            description: 'Warning text when a LuxPlus+ backup payment method expires before renewal',
          })}
        </BodyText>
      </ExpiryWarningNotice>}
    </BackupCardStack>
  </BackupCardRowShell>
}

export default LuxPlusBackupPaymentMethodRow
